import { useState } from "react";
import { CodeInput, PasswordInput } from "./AuthShell";
import { acceptAmount, chargedMoney, money } from "../lib/money";
import { useRecordExternalRefund } from "../lib/queries";
import type { ParkedPayment } from "../lib/types";
import { Modal } from "./Modal";
import { Alert, Button, Field, Input } from "./ui";

/**
 * A refund the processor cannot tell us about.
 *
 * PayPal and Polar report refunds by webhook and the ledger follows on its
 * own. BorderPay has no refund API: the rupiah goes back through its
 * dashboard or a bank transfer, and nothing reaches the platform -- so the
 * customer would keep the credit their refunded money bought. This records
 * it after the fact: the credit to take back, what actually went back in the
 * customer's currency, and the transfer's reference, which is what stops the
 * same refund being recorded twice.
 */
export function RecordRefundModal({ payment, onClose }: { payment: ParkedPayment; onClose: () => void }) {
  const remainingMicros = payment.amountMicros - payment.refundedMicros;
  const [amount, setAmount] = useState(String(remainingMicros / 1_000_000));
  const [charged, setCharged] = useState(
    payment.charged ? String(Math.round((payment.charged.amount * remainingMicros) / payment.amountMicros)) : "",
  );
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const mutation = useRecordExternalRefund();

  const amountCents = Math.round(Number(amount) * 100);
  const chargedAmount = charged.trim() === "" ? undefined : Number(charged);
  const ready = amountCents > 0 && amountCents * 10_000 <= remainingMicros && reference.trim().length >= 3 && password && code.length >= 6;

  return (
    <Modal
      open
      onClose={onClose}
      title="Record a refund"
      description={`${payment.organization.name} · ${money(payment.amountMicros, payment.currency)} of credit${payment.charged ? ` paid as ${chargedMoney(payment.charged.amount, payment.charged.currency)}` : ""}${payment.refundedMicros ? `, ${money(payment.refundedMicros, payment.currency)} already refunded` : ""}.`}
      dismissable={!mutation.isPending}
    >
      <div className="space-y-4">
        {mutation.error && <Alert>{mutation.error.message}</Alert>}
        <Alert tone="info">
          Send the money back through BorderPay first. This records what you sent so the credit it bought comes off the balance; it moves no money itself.
        </Alert>

        <Field label="Credit to take back" htmlFor="refund-amount" hint={`In the billing currency. At most ${money(remainingMicros, payment.currency)}.`}>
          <Input
            id="refund-amount"
            inputMode="decimal"
            value={amount}
            onChange={(event) => {
              const accepted = acceptAmount(event.target.value);
              if (accepted !== null) setAmount(accepted);
            }}
          />
        </Field>
        {payment.charged && (
          <Field label={`Sent back, in ${payment.charged.currency}`} htmlFor="refund-charged" hint="Whole units, as the transfer shows it.">
            <Input
              id="refund-charged"
              inputMode="numeric"
              value={charged}
              onChange={(event) => {
                if (/^\d*$/.test(event.target.value)) setCharged(event.target.value);
              }}
            />
          </Field>
        )}
        <Field label="Transfer reference" htmlFor="refund-reference" hint="Required. BorderPay's or the bank's id for the transfer. The same reference is never recorded twice.">
          <Input id="refund-reference" value={reference} onChange={(event) => setReference(event.target.value)} />
        </Field>
        <Field label="Note" htmlFor="refund-note" hint="Optional.">
          <Input id="refund-note" value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>

        <div className="space-y-3 border-t border-line pt-4">
          <p className="text-xs text-fg-subtle">Taking credit back moves money, so it asks for your password and authenticator code again.</p>
          <Field label="Password" htmlFor="refund-password">
            <PasswordInput id="refund-password" size="md" surface="panel" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} />
          </Field>
          <CodeInput id="refund-code" label="Authenticator code" value={code} onChange={setCode} />
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            disabled={!ready}
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate(
                {
                  paymentId: payment.id,
                  amountCents,
                  chargedAmount,
                  reference: reference.trim(),
                  note: note.trim() || undefined,
                  password,
                  code,
                },
                { onSuccess: onClose },
              )
            }
          >
            Record {money(amountCents * 10_000, payment.currency)} refunded
          </Button>
        </div>
      </div>
    </Modal>
  );
}
